import {
  AttrNumber,
  AttrRichTextEditor,
  BaseAttribute,
  ComponentKeyValue,
} from '../../types/attributes';
import {
  ATTR_NAMES,
  AttrValue,
  getAttrFromProduct,
} from '../../types/attrNames';
import { CmsProduct, GiftListItem } from '../../types/cmsProduct';
import { formatNumberWithSeparator } from './formatters';
import { PersonalLoanFilterConfig } from './types';

export const formatAttributeItemValue = (
  attr: BaseAttribute,
  addSpace: boolean = false,
  formatNumber: boolean = false,
  withoutPrefix: boolean = true,
): string => {
  switch (attr?.__typename) {
    case 'ComponentAttributesAttrNumber':
      if (!attr.value && attr.empty_value_text) {
        return attr.empty_value_text;
      }
      if (!attr.value) {
        return ' - ';
      }
      return withPrefixAndSuffix(
        formatNumber
          ? formatNumberWithSeparator({
              value: attr.value,
              thousandsSeparator: ',',
            })
          : attr.value,
        withoutPrefix ? attr.prefix : null,
        attr.suffix,
        addSpace,
      );

    case 'ComponentAttributesAttrNumberRange': {
      let val = '';
      if (attr.value_start) {
        val = attr.value_start.toString();
        if (attr.value_end) {
          val += ` - ${attr.value_end}`;
        }
      }
      return withPrefixAndSuffix(
        val,
        withoutPrefix ? attr.prefix : null,
        attr.suffix,
        addSpace,
      );
    }
    case 'ComponentAttributesAttrBoolean':
      return (attr.value ? attr.true_value : attr.false_value) ?? '';
    case 'ComponentAttributesAttrPlainText':
      return attr.value ?? '';
    case 'ComponentAttributesAttrRichTextEditor':
    case 'ComponentAttributesAttrJson':
      return attr.value ?? '';
    case 'ComponentAttributesAttrKeyValue': {
      const res = attr?.value?.reduce((acc, cur) => {
        if (acc) {
          return acc;
        }
        return cur?.values?.[0]?.value ?? '';
      }, '');

      return res ?? '';
    }
    default:
      return attr.value;
  }
};

/**
 * An attribute's value as a comparison cell prints it: thousands separated, the prefix set close to
 * the number (HK$1,500,000), and `undefined` rather than the formatter's placeholder dash where
 * there is no value. One rendering for every table that sets plans side by side, so the same plan
 * reads the same on the product page and on the comparison page.
 *
 * `formatAttributeItemValue` is typed `: string`, but its `default` arm returns `attr.value` raw,
 * and the payment/table/multi-text/add-on variants type `value` as `any`: a non-string resolves as
 * missing instead of throwing on `.trim`.
 */
export const attributeCellText = (attr: BaseAttribute): string | undefined => {
  const formatted = formatAttributeItemValue(attr, false, true);
  const text = typeof formatted === 'string' ? formatted.trim() : '';
  return text && text !== '-' ? text : undefined;
};

/** Shared read under the per-attribute helpers below: trimmed value, blank/absent → undefined. */
const getTrimmedAttrValue = (
  attrName: AttrValue,
  product?: CmsProduct,
): string | undefined => {
  const attr = getAttrFromProduct(attrName, product);

  if (!attr) {
    return undefined;
  }

  return formatAttributeItemValue(attr).trim() || undefined;
};

/**
 * Base value only, for callers that label the tag's source themselves (the
 * CC result tile); everything else renders through `resolveProductTag`.
 */
export const getProductTagValue = (product?: CmsProduct): string | undefined =>
  getTrimmedAttrValue(ATTR_NAMES.PRODUCT_TAG, product);

/**
 * The one precedence rule for the displayed Product Tag (CMS-1970): a page- or
 * module-authored tag wins over the product-level PRODUCT_TAG attribute. `||`
 * on purpose — a blank/whitespace override is "not set", not an empty tag.
 */
export const resolveProductTag = ({
  product,
  pageLevelTag,
}: {
  product?: CmsProduct;
  pageLevelTag?: string;
}): string | undefined => pageLevelTag?.trim() || getProductTagValue(product);

/**
 * Product-level Hot Reward display-text override (CMS-1969, plain text).
 * Callers decide precedence against derived reward text.
 */
export const getHotRewardOverrideValue = (
  product?: CmsProduct,
): string | undefined => getTrimmedAttrValue(ATTR_NAMES.HOT_REWARD, product);

function withPrefixAndSuffix(
  value?: string | null,
  prefix?: string | null,
  suffix?: string | null,
  addSpace: boolean = false,
) {
  let formattedValue = value ?? '';
  if (
    (prefix && addSpace) ||
    (/[a-zA-Z]/.test(prefix ?? '') && !/[$€¥₽£]/.test(prefix ?? ''))
  ) {
    formattedValue = `${prefix} ${value}`;
  }
  if (prefix && !addSpace) {
    formattedValue = `${prefix}${value}`;
  }

  if (suffix) {
    if (suffix.includes('%')) {
      formattedValue = `${formattedValue} ${suffix}`;
    } else {
      formattedValue = `${formattedValue} ${suffix}`;
    }
  }

  return formattedValue;
}

export function groupProductAttributes(product?: CmsProduct): AttributeGroups {
  const repayment = [
    'MINIMUM_PAYMENT',
    'LATE_PAYMENT_CHARGE',
    'INTEREST_ON_PURCHASES',
    'INTEREST_ON_CASH_ADVANCE',
    'INTEREST_FREE_PERIOD',
  ];

  const eligibility = [
    'MINIMUM_AGE',
    'MINIMUM_ANNUAL_INCOME',
    'MONTHLY_SPEND_REQUIRED',
  ];

  const benefits = ['CASHBACK_LOCAL', 'CASHBACK_BASELINE', 'CASHBACK_OVERSEAS'];

  const benefitsCashbackPrefix = 'CASHBACK_';
  const benefitsAirmilesPrefix = 'AIRMILES_';
  const benefitsRewardsPointsPrefix = 'REWARDS_POINTS_';

  const fees = [
    'ANNUAL_FEE',
    'FOREIGN_CURRENCY_TRANSACTION_FEE',
    'CASH_ADVANCE_SERVICE_CHARGE',
    'AIRMILES_CONVERSION_FEE',
  ];

  const primaryFees = [
    'ANNUAL_FEE',
    'FOREIGN_CURRENCY_TRANSACTION_FEE',
    'CASH_ADVANCE_SERVICE_CHARGE',
    'AIRMILES_CONVERSION_FEE',
  ];

  const fullFeeSchedule = [
    'INTEREST_RATE',
    'APR',
    'LATE_PAYMENT_CHARGE',
    'OVER_LIMIT_FEE',
    'REPLACEMENT_FEE',
    'PAPER_STATEMENT_FEE',
  ];

  const installmentFees = [
    'INSTALLMENT_RATE',
    'INSTALLMENT_TENURE',
    'MIN_SPENDING_INSTALMENTS',
    'PROCESSING_FEE',
    'INSURANCE_INSTALLMENT_TENURE',
  ];

  const requiredDocuments = ['REQUIRED_DOCUMENTS'];

  const attributesGroupInit = {
    repayment: [],
    eligibility: [],
    benefits: [],
    fees: [],
    requiredDocuments: [],
    primaryFees: [],
    fullFeeSchedule: [],
    installmentFees: [],
    benefitsCashback: [],
    benefitsAirmiles: [],
    benefitsRewardsPoints: [],
  };

  return (
    product?.attributes?.reduce<AttributeGroups>((accum, current) => {
      const name = current.name?.name ?? '';
      if (current?.__typename === 'ComponentAttributesAttrNumber') {
        if (repayment.includes(name)) {
          accum.repayment.push(current);
        }
        if (benefits.includes(name)) {
          accum.benefits.push(current);
        }
        if (eligibility.includes(name)) {
          accum.eligibility.push(current);
        }
        if (fees.includes(name)) {
          accum.fees.push(current);
        }
        if (primaryFees.includes(name)) {
          accum.primaryFees?.push(current);
        }
        if (fullFeeSchedule.includes(name)) {
          accum.fullFeeSchedule?.push(current);
        }
        if (installmentFees.includes(name)) {
          accum.installmentFees?.push(current);
        }
        if (name.startsWith(benefitsCashbackPrefix)) {
          accum.benefitsCashback?.push(current);
        }
        if (name.startsWith(benefitsAirmilesPrefix)) {
          accum.benefitsAirmiles?.push(current);
        }
        if (name.startsWith(benefitsRewardsPointsPrefix)) {
          accum.benefitsRewardsPoints?.push(current);
        }
      }

      if (current.__typename === 'ComponentAttributesAttrRichTextEditor') {
        if (requiredDocuments.includes(name)) {
          accum.requiredDocuments.push(current);
        }
      }

      return accum;
    }, attributesGroupInit) ?? attributesGroupInit
  );
}

type AttributeGroups = {
  repayment: AttrNumber[];
  eligibility: AttrNumber[];
  benefits: AttrNumber[];
  fees: AttrNumber[];
  primaryFees?: AttrNumber[];
  fullFeeSchedule?: AttrNumber[];
  installmentFees?: AttrNumber[];
  benefitsCashback?: AttrNumber[];
  benefitsAirmiles?: AttrNumber[];
  benefitsRewardsPoints?: AttrNumber[];
  requiredDocuments: AttrRichTextEditor[];
};

export function filterGiftsByAmountAndTenure(
  giftList: GiftListItem[] | undefined,
  amountNumber?: number,
  tenureNumber?: number,
  personalLoanFilterConfig?: PersonalLoanFilterConfig,
): {
  matchingAmountTenure: GiftListItem[];
  nonMatchingAmountTenure: GiftListItem[];
} {
  if (!giftList)
    return { matchingAmountTenure: [], nonMatchingAmountTenure: [] };

  const matchingAmountTenure: GiftListItem[] = [];
  const nonMatchingAmountTenure: GiftListItem[] = [];

  giftList.forEach((g) => {
    const { start: giftAmountStart, end: giftAmountEnd } = extractGiftRange(
      g.criteria_list,
      'ELIGIBLE_LOAN_RANGE',
    );
    const { start: giftTenureStart, end: giftTenureEnd } = extractGiftRange(
      g.criteria_list,
      'ELIGIBLE_TENURE_RANGE',
    );

    const invalidStartAmount =
      giftAmountStart &&
      personalLoanFilterConfig?.min_loan_amount !== undefined &&
      giftAmountStart < personalLoanFilterConfig.min_loan_amount;
    const invalidEndAmount =
      giftAmountEnd &&
      personalLoanFilterConfig?.max_loan_amount !== undefined &&
      giftAmountEnd > personalLoanFilterConfig.max_loan_amount;
    const invalidStartTenure =
      giftTenureStart &&
      personalLoanFilterConfig?.min_loan_tenure !== undefined &&
      giftTenureStart < personalLoanFilterConfig.min_loan_tenure;
    const invalidEndTenure =
      giftTenureEnd &&
      personalLoanFilterConfig?.max_loan_tenure !== undefined &&
      giftTenureEnd > personalLoanFilterConfig.max_loan_tenure;

    if (
      invalidStartAmount ||
      invalidEndAmount ||
      invalidStartTenure ||
      invalidEndTenure
    ) {
      return;
    }

    let isAmountEqual = true;
    let isLoanEqual = true;

    if (amountNumber !== undefined) {
      isAmountEqual =
        (giftAmountStart ? giftAmountStart <= amountNumber : true) &&
        (giftAmountEnd ? giftAmountEnd >= amountNumber : true);
    }

    if (tenureNumber !== undefined) {
      isLoanEqual =
        (giftTenureStart ? giftTenureStart <= tenureNumber : true) &&
        (giftTenureEnd ? giftTenureEnd >= tenureNumber : true);
    }

    if (isLoanEqual && isAmountEqual) {
      matchingAmountTenure.push(g);
    } else {
      nonMatchingAmountTenure.push(g);
    }
  });

  return {
    matchingAmountTenure,
    nonMatchingAmountTenure,
  };
}

export function extractGiftRange(
  criteriaList: GiftListItem['criteria_list'],
  criteriaName: string,
): GiftRange {
  const criteria = criteriaList?.find((c) => c.name?.name === criteriaName);

  return {
    start: isNaN(parseInt(criteria?.value_start ?? ''))
      ? null
      : parseInt(criteria!.value_start!),
    end: isNaN(parseInt(criteria?.value_end ?? ''))
      ? null
      : parseInt(criteria!.value_end!),
    suffix: criteria?.suffix,
    prefix: criteria?.prefix,
    label: criteria?.label,
    startLabel: criteria?.starting_point_label,
    endLabel: criteria?.ending_point_label,
  };
}

type GiftRange = {
  start: number | null;
  end: number | null;
  suffix?: string;
  prefix?: string;
  label?: string;
  startLabel?: string;
  endLabel?: string;
};

const getKeyValueAttr = (attrName: string, attrs?: BaseAttribute[]) => {
  const attr = attrs?.find((a) => a?.name?.name === attrName);

  if (attr?.__typename !== 'ComponentAttributesAttrKeyValue') {
    return undefined;
  }

  return attr?.value;
};

const extractValuesKeyValue = (
  keyValueList: ComponentKeyValue[] | undefined,
  type: 'pros' | 'cons' | 'usp_labels',
): string[] => {
  return (
    keyValueList
      ?.find((v) => v?.key === type)
      ?.values?.map((v) => v?.value)
      .filter((v): v is string => Boolean(v)) ?? []
  );
};

type ProsCons = {
  pros: string[];
  cons: string[];
};

export const getQuickFacts = (
  attrs?: BaseAttribute[],
): { quickFacts: ProsCons; thingsConsider: ProsCons } => {
  const attrQuickFacts = getKeyValueAttr('QUICK_FACTS', attrs);
  const attrThingsConsider = getKeyValueAttr('THINGS_TO_CONSIDER', attrs);

  return {
    quickFacts: {
      pros: extractValuesKeyValue(attrQuickFacts, 'pros'),
      cons: extractValuesKeyValue(attrQuickFacts, 'cons'),
    },
    thingsConsider: {
      pros: extractValuesKeyValue(attrThingsConsider, 'pros'),
      cons: extractValuesKeyValue(attrThingsConsider, 'cons'),
    },
  };
};

export const getTIUniqueSellingPoint = (
  attrs?: BaseAttribute[],
): { uniqueSellingPoint: Partial<ProsCons> } => {
  const attrTIUniqueSellingPoint = getKeyValueAttr(
    'TI_UNIQUE_SELLING_POINTS',
    attrs,
  );

  return {
    uniqueSellingPoint: {
      pros: extractValuesKeyValue(attrTIUniqueSellingPoint, 'usp_labels'),
    },
  };
};

export const getTopBadge = (attrs?: BaseAttribute[]): string[] | undefined => {
  const attrValue = getKeyValueAttr('PRODUCT_CARD_TOP_BADGE', attrs);
  return attrValue?.[0]?.values
    ?.filter((v) => !!v?.value)
    .map((v) => v!.value!);
};
